import axios, { AxiosError } from "axios";

const COGNITO_AMAZONAWS_COM = "cognito-idp";

const COGNITO_TARGETS = {
  signUp: "AWSCognitoIdentityProviderService.SignUp",
  initiateAuth: "AWSCognitoIdentityProviderService.InitiateAuth",
} as const;

type CognitoAttribute = {
  Name: string;
  Value: string;
};

type CognitoServiceOptions = {
  /** Регион Cognito User Pool, по умолчанию eu-central-1 */
  region?: string;
  /** Таймаут запроса, мс. По умолчанию COGNITO_REQUEST_TIMEOUT_MS. */
  timeoutMs?: number;
};

/**
 * Таймаут запроса к Cognito.
 *
 * Эндпоинт живёт на cognito-idp.<region>.amazonaws.com — иностранный хост, недостижимый там,
 * где оператор включил белые списки доменов. Без таймаута зависший запрос держался до таймаута ОС
 * (десятки секунд), а вызывался он на старте приложения и внутри привязки профиля — то есть
 * блокировал появление экрана. POST идёт с заголовком X-Amz-Target, поэтому браузер шлёт ещё и
 * preflight: это два round-trip, а не один.
 */
const COGNITO_REQUEST_TIMEOUT_MS = 6000;

export type CreateCognitoServiceInput = {
  clientId: string;
  options?: CognitoServiceOptions;
};

export type CognitoRefreshResult = {
  idToken: string;
  refreshToken?: string;
};

export type CognitoSignUpInput = {
  username: string;
  /** Телефон для атрибута phone_number в формате E.164, например +79991234567 */
  phoneNumber?: string;
  password?: string;
  attributes?: CognitoAttribute[];
};

export type CognitoSignUpResult = {
  status: "created" | "exists";
  username: string;
  userConfirmed?: boolean;
  userSub?: string;
};

const randomUint32 = (): number => {
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return bytes.at(0) ?? 0;
};

const buildRandomPassword = (length = 24): string => {
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const digits = "0123456789";
  const symbols = "!@#$%^&*()-_=+[]{}:;,.?";
  const all = `${lower}${upper}${digits}${symbols}`;

  const pick = (source: string): string => {
    const index = randomUint32() % source.length;
    return source.charAt(index);
  };

  const required = [pick(lower), pick(upper), pick(digits), pick(symbols)];
  const randomTail = Array.from({ length: Math.max(length - required.length, 0) }, () => pick(all));
  const raw = [...required, ...randomTail];

  for (let i = raw.length - 1; i > 0; i -= 1) {
    const j = randomUint32() % (i + 1);
    const left = raw[i];
    const right = raw[j];

    if (left === undefined || right === undefined) {
      continue;
    }

    raw[i] = right;
    raw[j] = left;
  }

  return raw.join("");
};

const normalizeCognitoErrorCode = (error: unknown): string | undefined => {
  if (!(error instanceof AxiosError)) {
    return;
  }

  const data = error.response?.data as { __type?: string; code?: string } | undefined;
  const source = data?.code ?? data?.__type;
  if (!source) {
    return;
  }

  const code = source.split("#").at(-1);
  return code?.trim();
};

type CognitoRequestPayload = Record<string, unknown>;

class CognitoService {
  private readonly clientId: string;
  private readonly region: string;
  private readonly timeoutMs: number;

  constructor({ clientId, options }: CreateCognitoServiceInput) {
    this.clientId = clientId;
    this.region = options?.region ?? "eu-central-1";
    this.timeoutMs = options?.timeoutMs ?? COGNITO_REQUEST_TIMEOUT_MS;
  }

  private get endpoint(): string {
    return `https://${COGNITO_AMAZONAWS_COM}.${this.region}.amazonaws.com/`;
  }

  private async request<Response>(target: string, payload: CognitoRequestPayload): Promise<Response> {
    const response = await axios.post<Response>(this.endpoint, payload, {
      timeout: this.timeoutMs,
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": target,
      },
    });

    return response.data;
  }

  async signUp({ username, phoneNumber, password, attributes = [] }: CognitoSignUpInput): Promise<CognitoSignUpResult> {
    const userAttributes: CognitoAttribute[] = [...attributes];

    if (phoneNumber) {
      userAttributes.push({ Name: "phone_number", Value: phoneNumber });
    }

    const payload = {
      ClientId: this.clientId,
      Username: username,
      Password: password ?? buildRandomPassword(),
      ClientMetadata: {
        flow: "autoconfirm",
      },
      ...(userAttributes.length > 0 ? { UserAttributes: userAttributes } : {}),
    };

    try {
      const response = await this.request<{
        UserConfirmed?: boolean;
        UserSub?: string;
      }>(COGNITO_TARGETS.signUp, payload);

      return {
        status: "created",
        username,
        userSub: response.UserSub ?? username,
        ...(response.UserConfirmed === undefined ? {} : { userConfirmed: response.UserConfirmed }),
      };
    } catch (error) {
      const code = normalizeCognitoErrorCode(error);
      if (code === "UsernameExistsException") {
        return {
          status: "exists",
          username,
        };
      }

      throw error;
    }
  }

  async refreshToken(refreshToken: string): Promise<CognitoRefreshResult | null> {
    const response = await this.request<{
      AuthenticationResult?: {
        IdToken?: string;
        RefreshToken?: string;
      };
    }>(COGNITO_TARGETS.initiateAuth, {
      AuthFlow: "REFRESH_TOKEN_AUTH",
      ClientId: this.clientId,
      AuthParameters: {
        REFRESH_TOKEN: refreshToken,
      },
    });

    const result = response.AuthenticationResult;

    if (!result?.IdToken) {
      return null;
    }

    return {
      idToken: result.IdToken,
      ...(result.RefreshToken === undefined ? {} : { refreshToken: result.RefreshToken }),
    };
  }
}

export const createCognitoService = ({ clientId, options }: CreateCognitoServiceInput): CognitoService =>
  new CognitoService({
    clientId,
    ...(options ? { options } : {}),
  });
